import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { User } from '@xver/@types/user';
import type { UpdateMeRequestModel } from '@xver/apis/v1/users/model';
import { InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';

const PHONE_REGEX = /^0[0-9]{9}$/;

interface ProfileEditFormProps {
    user: User;
    onSubmit: (payload: UpdateMeRequestModel) => Promise<unknown>;
    isSubmitting?: boolean;
}

interface FormErrors {
    phoneNumber?: string;
}

type FormTouched = Partial<Record<keyof FormErrors, boolean>>;

export default function ProfileEditForm({ user, onSubmit, isSubmitting }: ProfileEditFormProps) {
    const { t } = useTranslation('profile');

    const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '');
    const [touched, setTouched] = useState<FormTouched>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    useEffect(() => {
        setPhoneNumber(user.phoneNumber ?? '');
        setTouched({});
        setSubmitAttempted(false);
    }, [user.id, user.phoneNumber]);

    const validation = useMemo<FormErrors>(() => {
        const errors: FormErrors = {};
        const phone = phoneNumber.trim();
        if (!phone) errors.phoneNumber = t('errors.phoneNumberRequired');
        else if (!PHONE_REGEX.test(phone)) errors.phoneNumber = t('errors.phoneNumberInvalid');
        return errors;
    }, [phoneNumber, t]);

    const visibleErrors = useMemo<FormErrors>(() => {
        const out: FormErrors = {};
        (Object.keys(validation) as Array<keyof FormErrors>).forEach((key) => {
            if (submitAttempted || touched[key]) out[key] = validation[key];
        });
        return out;
    }, [validation, touched, submitAttempted]);

    const isDirty = phoneNumber.trim() !== (user.phoneNumber ?? '');

    const handleSubmit = async () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (Object.keys(validation).length > 0) return;
        const payload: UpdateMeRequestModel = {};
        if (phoneNumber.trim() !== (user.phoneNumber ?? '')) {
            payload.phoneNumber = phoneNumber.trim();
        }
        if (Object.keys(payload).length === 0) return;
        await onSubmit(payload);
    };

    return (
        <DialogBox
            label={t('edit.title')}
            description={t('edit.description')}
            classNames="p-4 gap-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                    size="md"
                    label={t('edit.firstName')}
                    value={user.information.firstName}
                    disabled
                    descriptionBottom={t('edit.nameLockedHint')}
                />
                <InputField
                    size="md"
                    label={t('edit.lastName')}
                    value={user.information.lastName}
                    disabled
                    descriptionBottom={t('edit.nameLockedHint')}
                />
                <InputField
                    size="md"
                    label={t('edit.phoneNumber')}
                    value={user.phoneNumber}
                    disabled
                    descriptionBottom={t('edit.phoneNumberDescription')}
                />
                <InputField
                    size="md"
                    label={t('edit.email')}
                    value={user.email}
                    disabled
                    descriptionBottom={t('edit.emailHint')}
                />
            </div>
        </DialogBox>
    );
}
